import React, { useState, useEffect, useRef } from "react";
import PageLayout from "../../../Layouts/PageLayout/PageLayout";
import Table from "../../../components/common/Table/Table";
import Button from "../../../components/common/Button/Button";
import { getApiInstance } from "../../../utils/api";
import { ZONE_LABEL_MAP, ZONE_OPTIONS } from "../../../constants/zones";
import { getZoneLabel } from "../../../utils/zoneLabels";
import { useTranslation } from "react-i18next";
import { IoMdAdd } from "react-icons/io";

export default function Intermediarios({ user }) {
    const { t } = useTranslation();
    const tableRef = useRef(null);
    const canAdd = Number(user.codAcceso) === 1 && Number(user.role) !== 4;
    const [intermediarios, setIntermediarios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const cifLabel = t("fields.cif");
    const emailLabel = t("fields.email");
    const localityLabel = t("fields.locality");
    const localityPlaceholder = t("intermediaries.form.localityPlaceholder");
    const cifPlaceholder = t("intermediaries.form.cifPlaceholder");
    const emailPlaceholder = t("intermediaries.form.emailPlaceholder");
    const filterCifLabel = t("intermediaries.columns.cif");

    const filterFields = [
        {
            key: "nombre",
            label: t("intermediaries.columns.name"),
            type: "multiselect",
        },
        {
            key: "contacto",
            label: t("intermediaries.columns.contact"),
            type: "multiselect",
        },
        { key: "codZona", label: "Zona", type: "multiselect", labelMap: ZONE_LABEL_MAP },
    ];

    const columnasIntermediarios = [
        { key: "id", label: "codIntermediario", visible: false },
        { key: "codIntermediario", label: "codIntermediario", visible: false },
        {
            key: "nombre",
            label: t("fields.name"),
            visible: true,
            sortable: true,
        },
        {
            key: "cif",
            label: cifLabel,
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
        {
            key: "contacto",
            label: t("fields.contact"),
            visible: true,
            sortable: true,
            visibleOnExpand: true,
            render: (row) => {
                return (
                    <div className="table-cell-content">
                        <span className="table-cell-content__primary">
                            {row.contacto || "-"}
                        </span>
                        <span className="table-cell-content__secondary">
                            {row.eMail || ""}
                        </span>
                    </div>
                );
            },
        },
        {
            key: "telefono",
            label: t("fields.phone"),
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
        {
            key: "eMail",
            label: emailLabel,
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
    ];

    useEffect(() => {
        fetchIntermediarios();
    }, []);

    const fetchIntermediarios = async () => {
        setLoading(true);
        const api = getApiInstance();

        try {
            let intermediariosData = [];

            const response = await api.get(
                `/api/Intermediarios/GetIntermediarios`
            );
            intermediariosData = response.data;

            setIntermediarios(intermediariosData);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar los intermediarios. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <PageLayout
            title={t("intermediaries.title")}
            subtitle={t("intermediaries.subtitle")}
            actions={[
                ...(canAdd
                    ? [
                          <Button
                              key="add"
                              variant="filled"
                              startIcon={IoMdAdd({})}
                              onClick={() => tableRef.current?.openAddModal()}
                              endIcon={undefined}
                          >
                              {`${t("common.add")} ${t("fields.intermediary")}`}
                          </Button>,
                      ]
                    : []),
            ]}
        >
            <Table
                ref={tableRef}
                hideAddButton={true}
                columns={columnasIntermediarios}
                dataTable={intermediarios}
                showCheckboxes={false}
                expandable={true}
                expandableBreakpoint={600}
                noResultsMessage={t("intermediaries.noIntermediariesFound")}
                actions={["Eliminar", "Editar"]}
                searchTerms={["nombre", "cif", "contacto", "telefono", "eMail"]}
                elementName={t("fields.intermediary")}
                formElements={[
                    {
                        elements: [
                            {
                                name: "nombre",
                                type: "text",
                                label: t("fields.name"),
                                placeholder: t(
                                    "intermediaries.form.namePlaceholder"
                                ),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "cif",
                                type: "text",
                                label: cifLabel,
                                placeholder: cifPlaceholder,
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "direccion",
                                type: "text",
                                label: t("fields.address"),
                                placeholder: t(
                                    "intermediaries.form.addressPlaceholder"
                                ),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "localidad",
                                type: "text",
                                label: localityLabel,
                                placeholder: localityPlaceholder,
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "contacto",
                                type: "tel",
                                label: t("fields.contact"),
                                placeholder: t(
                                    "intermediaries.form.contactPlaceholder"
                                ),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "telefono",
                                type: "tel",
                                label: t("fields.phone"),
                                placeholder: t(
                                    "intermediaries.form.phonePlaceholder"
                                ),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "eMail",
                                type: "email",
                                label: emailLabel,
                                placeholder: emailPlaceholder,
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "codZona",
                                type: "select",
                                label: "Zona",
                                placeholder: "Selecciona la zona",
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                                options: ZONE_OPTIONS,
                            },
                        ],
                    },
                ]}
                filterFields={filterFields}
                loading={loading}
                keyProp="codIntermediario"
                sortProp="nombre"
                addApiUrl="/api/Intermediarios/SetIntermediario"
                editApiUrl="/api/Intermediarios/UpdateIntermediario"
                deleteApiUrl="/api/Intermediarios/DeleteIntermediario"
                userCodAcceso={Number(user.codAcceso)}
                userCodPerfil={Number(user.role)}
            />
        </PageLayout>
    );
}
